import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { WireTypes } from '@flakiness/server/common/wireTypes.js';
import { Temporal } from 'temporal-polyfill';
import { PlaneName } from './features/feature-tests-report.js';
import { URLState } from './router.js';

const PLANES: PlaneName[] = ['tests', 'timelines', 'errors', 'tags', 'annotations'];

/** URL options that several pages share, so that they mean the same on every page. */
export namespace URLOptions {
  // Absent means "All Environments": the default split that the page fetches.
  export const timelineSplit = URLState.option<EnvGroup|undefined>({
    name: 'split',
    default: undefined,
    encode: split => split ? JSON.stringify(split.serialize()) : undefined,
    decode: text => EnvGroup.deserialize(JSON.parse(text)),
  });

  export const testTimeline = URLState.option<EnvGroup|undefined>({
    name: 'test_timeline',
    default: undefined,
    encode: timeline => timeline ? JSON.stringify(timeline.serialize()) : undefined,
    decode: text => EnvGroup.deserialize(JSON.parse(text)),
  });

  export const testAttempt = URLState.option<number>({
    name: 'test_attempt',
    default: 0,
    encode: attemptIdx => attemptIdx ? String(attemptIdx) : undefined,
    decode: attemptText => attemptText ? parseInt(attemptText, 10) : 0,
  });

  export const query = URLState.option<string>({
    name: 'q',
    default: '',
    encode: filter => filter,
    decode: filter => filter,
  });

  // Pages decide which of the planes they show.
  export const plane = URLState.option<PlaneName|undefined>({
    name: 'tab',
    default: undefined,
    encode: plane => plane,
    decode: text => PLANES.find(plane => plane === text),
  });

  export const page = URLState.option<number>({
    name: 'page',
    default: 0,
    // keep pages one-based in url for user's pleasure.
    encode: page => String(page + 1),
    decode: page => isNaN(parseInt(page, 10)) ? 1 : parseInt(page, 10) - 1,
  });

  export const pageSize = URLState.option<number>({
    name: 'page_size',
    default: 20,
    encode: pageSize => String(pageSize),
    decode: pageSize => isNaN(parseInt(pageSize, 10)) ? 1 : parseInt(pageSize, 10),
  });

  export const sortName = URLState.option<string|undefined>({
    name: 'sort_by',
    default: undefined,
    encode: sort => sort,
    decode: sort => sort,
  });

  export const sortDirection = URLState.option<WireTypes.SortDirection|undefined>({
    name: 'sort_dir',
    default: undefined,
    encode: sort => sort,
    decode: sort => sort === 'asc' || sort === 'desc' ? sort as WireTypes.SortDirection : undefined,
  });

  // Local override of the project's default_history_type. Absent => follow the project default.
  export const historyType = URLState.option<WireTypes.HistoryType|undefined>({
    name: 'history',
    default: undefined,
    encode: type => type,
    decode: type => WireTypes.HISTORY_TYPES.find(historyType => historyType === type),
  });

  export const head = URLState.option<string|undefined>({
    name: 'head',
    default: undefined,
    encode: head => head,
    decode: head => head,
  });

  export const until = URLState.option<Temporal.PlainDate|undefined>({
    name: 'until',
    default: undefined,
    encode: d => d?.toString(),
    decode: txt => txt ? Temporal.PlainDate.from(txt) : undefined,
  });

  export const viewSince = URLState.option<Temporal.PlainDate|undefined>({
    name: 'view_since',
    default: undefined,
    encode: d => d?.toString(),
    decode: txt => txt ? Temporal.PlainDate.from(txt) : undefined,
  });

  export const viewUntil = URLState.option<Temporal.PlainDate|undefined>({
    name: 'view_until',
    default: undefined,
    encode: d => d?.toString(),
    decode: txt => txt ? Temporal.PlainDate.from(txt) : undefined,
  });
}
